import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Shield, ArrowRight, CheckCircle, AlertTriangle, XCircle,
  TrendingUp, Target, Phone, Mail, Calendar, Download,
  Users, Lock, Database, Server, Activity, Brain
} from 'lucide-react';

// Same questions for calculating results
const surveyQuestions = [
  { id: 'dod_engagement', section: 'DoD Engagement', isPrequalifier: true },
  { id: 'q1', section: 'Policies & Awareness', maxPoints: 3 },
  { id: 'q2', section: 'Policies & Awareness', maxPoints: 3 },
  { id: 'q3', section: 'Policies & Awareness', maxPoints: 3 },
  { id: 'q4', section: 'Access Controls', maxPoints: 3 },
  { id: 'q5', section: 'Access Controls', maxPoints: 3 },
  { id: 'q6', section: 'Access Controls', maxPoints: 3 },
  { id: 'q7', section: 'Data Protection', maxPoints: 3 },
  { id: 'q8', section: 'Data Protection', maxPoints: 3 },
  { id: 'q9', section: 'Incident Response', maxPoints: 3 },
  { id: 'q10', section: 'Incident Response', maxPoints: 3 },
  { id: 'q11', section: 'Incident Response', maxPoints: 3 },
  { id: 'q12', section: 'Systems & Network', maxPoints: 3 },
  { id: 'q13', section: 'Systems & Network', maxPoints: 3 },
  { id: 'q14', section: 'Systems & Network', maxPoints: 3 },
  { id: 'q15', section: 'Continuous Monitoring', maxPoints: 3 },
  { id: 'q16', section: 'Continuous Monitoring', maxPoints: 3 },
  { id: 'q17', section: 'Continuous Monitoring', maxPoints: 3 },
];

const pointValues = {
  'full': 3, 'yes': 3,
  'partial': 1, 'not_yet': 1,
  'no': 0
};

const sectionIcons = {
  'Policies & Awareness': Users,
  'Access Controls': Lock,
  'Data Protection': Database,
  'Incident Response': AlertTriangle,
  'Systems & Network': Server,
  'Continuous Monitoring': Activity
};

const SelfAssessmentResults = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [answers, setAnswers] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/self-assessment/results' } });
      return;
    }

    // Load answers from localStorage
    const savedAnswers = localStorage.getItem('selfAssessmentAnswers');
    if (savedAnswers) {
      const parsedAnswers = JSON.parse(savedAnswers);
      setAnswers(parsedAnswers);
      calculateResults(parsedAnswers);
    } else {
      // No saved answers, redirect to assessment
      navigate('/self-assessment');
    }
  }, [isAuthenticated, navigate]);

  const calculateResults = (answers) => {
    let totalPoints = 0;
    let maxPoints = 0;
    const sectionScores = {};

    // Calculate scores
    surveyQuestions.forEach(q => {
      if (q.isPrequalifier) return;
      
      const answer = answers[q.id];
      const points = pointValues[answer] || 0;
      
      totalPoints += points;
      maxPoints += q.maxPoints;

      if (!sectionScores[q.section]) {
        sectionScores[q.section] = { points: 0, maxPoints: 0 };
      }
      sectionScores[q.section].points += points;
      sectionScores[q.section].maxPoints += q.maxPoints;
    });

    // Determine readiness level
    let readinessLevel, readinessColor, readinessDescription;
    const percentage = (totalPoints / maxPoints) * 100;

    if (totalPoints <= 17) {
      readinessLevel = 'Low Readiness';
      readinessColor = 'red';
      readinessDescription = 'Your organization needs significant improvements to meet CMMC requirements. We recommend starting with foundational security policies and controls.';
    } else if (totalPoints <= 34) {
      readinessLevel = 'Medium Readiness';
      readinessColor = 'yellow';
      readinessDescription = 'Your organization has some security measures in place but needs to address gaps. Focus on formalizing policies and implementing consistent controls.';
    } else {
      readinessLevel = 'High Readiness';
      readinessColor = 'green';
      readinessDescription = 'Your organization demonstrates strong cybersecurity practices. Continue to refine and document your controls for CMMC certification.';
    }

    // Identify weak areas (sections scoring below 66%)
    const weakAreas = Object.entries(sectionScores)
      .filter(([_, score]) => (score.points / score.maxPoints) < 0.66)
      .map(([section, score]) => ({
        section,
        score: Math.round((score.points / score.maxPoints) * 100),
        points: score.points,
        maxPoints: score.maxPoints
      }))
      .sort((a, b) => a.score - b.score);

    // Identify strong areas (sections scoring 66% or above)
    const strongAreas = Object.entries(sectionScores)
      .filter(([_, score]) => (score.points / score.maxPoints) >= 0.66)
      .map(([section, score]) => ({
        section,
        score: Math.round((score.points / score.maxPoints) * 100),
        points: score.points,
        maxPoints: score.maxPoints
      }))
      .sort((a, b) => b.score - a.score);

    setResults({
      totalPoints,
      maxPoints,
      percentage: Math.round(percentage),
      readinessLevel,
      readinessColor,
      readinessDescription,
      sectionScores,
      weakAreas,
      strongAreas,
      dodEngagement: answers.dod_engagement === 'yes'
    });
  };

  if (!results) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const colorClasses = {
    red: {
      bg: 'bg-red-500',
      bgLight: 'bg-red-500/20',
      text: 'text-red-400',
      border: 'border-red-500/30'
    },
    yellow: {
      bg: 'bg-yellow-500',
      bgLight: 'bg-yellow-500/20',
      text: 'text-yellow-400',
      border: 'border-yellow-500/30'
    },
    green: {
      bg: 'bg-green-500',
      bgLight: 'bg-green-500/20',
      text: 'text-green-400',
      border: 'border-green-500/30'
    }
  };

  const colors = colorClasses[results.readinessColor];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 rounded-full text-violet-300 text-sm mb-6 border border-violet-500/30">
              <Brain className="w-4 h-4" />
              CMMC Readiness Assessment Results
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your Security Assessment Results
            </h1>
            <p className="text-violet-200">
              {user?.firstName ? `Hello ${user.firstName}, here` : 'Here'}'s your personalized CMMC readiness report.
            </p>
          </div>

          {/* Overall Score Card */}
          <div className={`bg-slate-800/80 backdrop-blur-lg rounded-2xl p-8 border ${colors.border} mb-8`}>
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Score Circle */}
              <div className="relative">
                <svg className="w-40 h-40 transform -rotate-90">
                  <circle
                    className="text-slate-700"
                    strokeWidth="12"
                    stroke="currentColor"
                    fill="transparent"
                    r="58"
                    cx="80"
                    cy="80"
                  />
                  <circle
                    className={colors.text}
                    strokeWidth="12"
                    strokeDasharray={`${results.percentage * 3.64} 364`}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r="58"
                    cx="80"
                    cy="80"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold text-white">{results.percentage}%</span>
                  <span className="text-slate-400 text-sm">Score</span>
                </div>
              </div>

              {/* Score Details */}
              <div className="flex-1 text-center md:text-left">
                <div className={`inline-flex items-center gap-2 px-4 py-2 ${colors.bgLight} rounded-full ${colors.text} text-sm font-medium mb-4`}>
                  {results.readinessColor === 'green' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : results.readinessColor === 'yellow' ? (
                    <AlertTriangle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {results.readinessLevel}
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {results.totalPoints} / {results.maxPoints} Points
                </h2>
                <p className="text-violet-200">{results.readinessDescription}</p>
                
                {results.dodEngagement && (
                  <div className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
                    <p className="text-blue-300 text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Working with DoD: CMMC compliance will be required for your contracts.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section Breakdown */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Weak Areas */}
            {results.weakAreas.length > 0 && (
              <div className="bg-slate-800/80 backdrop-blur-lg rounded-2xl p-6 border border-red-500/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Areas Needing Improvement
                </h3>
                <div className="space-y-4">
                  {results.weakAreas.map((area) => {
                    const Icon = sectionIcons[area.section] || Shield;
                    return (
                      <div key={area.section} className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-red-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">{area.section}</span>
                            <span className="text-red-400 text-sm">{area.score}%</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-2 mt-1">
                            <div 
                              className="bg-red-500 h-2 rounded-full"
                              style={{ width: `${area.score}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Strong Areas */}
            {results.strongAreas.length > 0 && (
              <div className="bg-slate-800/80 backdrop-blur-lg rounded-2xl p-6 border border-green-500/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Your Strengths
                </h3>
                <div className="space-y-4">
                  {results.strongAreas.map((area) => {
                    const Icon = sectionIcons[area.section] || Shield;
                    return (
                      <div key={area.section} className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-green-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-medium">{area.section}</span>
                            <span className="text-green-400 text-sm">{area.score}%</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-2 mt-1">
                            <div 
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${area.score}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Recommendations */}
          <div className="bg-slate-800/80 backdrop-blur-lg rounded-2xl p-8 border border-violet-500/20 mb-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-violet-400" />
              Next Steps & Recommendations
            </h3>
            <div className="space-y-4">
              {results.readinessColor === 'red' && (
                <>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Establish Foundational Policies</h4>
                      <p className="text-slate-300 text-sm">Create and document basic IT security policies covering acceptable use, access control, and data protection.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Implement Basic Access Controls</h4>
                      <p className="text-slate-300 text-sm">Ensure unique user accounts, implement MFA for critical systems, and establish access revocation procedures.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Schedule a Gap Assessment</h4>
                      <p className="text-slate-300 text-sm">Work with a CMMC-certified assessor to identify specific compliance gaps and create a remediation roadmap.</p>
                    </div>
                  </div>
                </>
              )}
              {results.readinessColor === 'yellow' && (
                <>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Formalize and Document Processes</h4>
                      <p className="text-slate-300 text-sm">Convert informal practices into documented procedures. Ensure all security policies are written and approved.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Address Your Weak Areas</h4>
                      <p className="text-slate-300 text-sm">Focus on improving {results.weakAreas.length > 0 ? results.weakAreas[0].section : 'your lowest scoring areas'} first, as this will have the biggest impact on your overall score.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Implement Regular Testing</h4>
                      <p className="text-slate-300 text-sm">Conduct tabletop exercises and test your incident response plan. Schedule regular internal audits.</p>
                    </div>
                  </div>
                </>
              )}
              {results.readinessColor === 'green' && (
                <>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Prepare for Formal Assessment</h4>
                      <p className="text-slate-300 text-sm">Gather evidence and documentation to support your security practices. Consider a pre-assessment review.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Maintain Continuous Monitoring</h4>
                      <p className="text-slate-300 text-sm">Keep your security practices current with regular reviews, updates, and employee training refreshers.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Document Everything</h4>
                      <p className="text-slate-300 text-sm">Ensure all policies, procedures, and evidence of compliance are well-organized and easily accessible for assessors.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-violet-600/20 to-purple-600/20 rounded-2xl p-8 border border-violet-500/30 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Take the Next Step?</h3>
            <p className="text-violet-200 mb-6 max-w-2xl mx-auto">
              Our team of cybersecurity experts can help you address your gaps, prepare for CMMC certification, and strengthen your security posture.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all"
              >
                <Shield className="w-5 h-5" />
                Start Full Assessment
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="mailto:contact@aspiritech.org"
                className="flex items-center gap-2 px-6 py-3 border border-violet-500/50 text-violet-200 hover:bg-violet-500/10 font-semibold rounded-xl transition-all"
              >
                <Mail className="w-5 h-5" />
                Contact Our Team
              </a>
            </div>
          </div>

          {/* Retake Assessment */}
          <div className="text-center mt-8">
            <button
              onClick={() => {
                localStorage.removeItem('selfAssessmentAnswers');
                navigate('/self-assessment');
              }}
              className="text-violet-400 hover:text-violet-300 transition-colors text-sm"
            >
              Retake Assessment →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfAssessmentResults;
